import {
  ExpectedRow,
  PASS_CATEGORY_LABELS,
} from '../../../../interfaces/visitor-pass';
import {
  clockTime,
  deviceZone,
  sameClock,
  zoneLabel,
} from '../../../../function-utils/building-time';
import { formatDate } from '../../../../function-utils/locale';
import { StatusTone } from '../../../status-pill/status-pill';
import { nameAtoms, nameTokens } from '../../visitor-exit/visitor-exit-match';

/**
 * EXPECTED TODAY, AS RULES — what the desk's Expected card and screen say about
 * a row of GET /buildings/:b/expected, kept pure so the menu, the screen and
 * their tests read ONE answer (resident-portal Plan 2, RP-2.11).
 *
 * THE SERVER DECIDES, THIS ONLY DESCRIBES. Whether a visit is signable now, and
 * whether it is early or late, is `window_state`, computed on the SERVER clock
 * (R-3: tablet clocks drift). Nothing here re-derives it from the tablet's
 * clock. The one place a clock is read — "later today" against "window ended"
 * for a row the server says is not signable — uses the response's own
 * `generated_at`, so it is the server's clock too.
 */

/** Two occurrences of one overnight pass can share a desk day (last night's and
 *  tonight's), so the pass id alone does not name a row. */
export const rowKey = (row: ExpectedRow): string =>
  `${row.pass_id}:${row.starts_at}`;

/** The DOM id a `?pass=` deep link scrolls to. */
export const rowDomId = (row: ExpectedRow): string =>
  `expected-${row.pass_id}-${Date.parse(row.starts_at) || 0}`;

export const fullNameOf = (row: ExpectedRow): string =>
  `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'Visitor (no name recorded)';

/** "Maria" in "Confirm: sign Maria in". */
export const firstNameOf = (row: ExpectedRow): string =>
  (row.first_name || '').trim() || 'the visitor';

export const categoryLabel = (row: ExpectedRow): string =>
  PASS_CATEGORY_LABELS[row.category] || PASS_CATEGORY_LABELS.other;

/** The one-tap sign-in is on offer: the server says the visit is signable now
 *  and the pass still has room for somebody. */
export const isSignable = (row: ExpectedRow): boolean =>
  row.window_state !== null && row.party_remaining > 0;

/** Somebody on this pass is inside, and the arrival to close is known. */
export const isSignOutable = (row: ExpectedRow): boolean =>
  row.arrival_state === 'inside' && row.arrival_id != null;

/**
 * THE MAIN-MENU BADGE: rows signable now that nobody has arrived for yet.
 *
 * Not "rows today": a visit due at 18:00 is not something the desk can act on
 * at 09:00, and a badge that counts it is a number nobody can clear.
 */
export const signableNowCount = (rows: ExpectedRow[] | null | undefined): number =>
  (rows || []).filter(
    row => row.window_state !== null && row.arrival_state === 'not_arrived'
  ).length;

/**
 * THE CARD'S GATE. Three answers, all required:
 *   - the person may read passes (VISITORPASS_READ, what GET /expected checks);
 *   - the building list says this building accepts passes;
 *   - the live /expected answer has not since said it does NOT. The building
 *     list is cached for the session; the /expected response is not, so a
 *     manager switching passes off hides the card on the desk's next read.
 * `livePassesEnabled` is null until the first read answers, which leaves the
 * building list's word standing.
 */
export const expectedCardVisible = ({
  canReadPasses,
  buildingAcceptsPasses,
  livePassesEnabled,
}: {
  canReadPasses: boolean;
  buildingAcceptsPasses: boolean;
  livePassesEnabled: boolean | null;
}): boolean => canReadPasses && buildingAcceptsPasses && livePassesEnabled !== false;

/**
 * THE SERVER'S ORDER, restated: inside LAST (they are handled), then by when
 * they are due, then by unit. The response already arrives sorted this way;
 * sorting again costs nothing and means a list patched between reads can never
 * put somebody already inside above the people still to come.
 */
export const sortExpected = (rows: ExpectedRow[]): ExpectedRow[] =>
  rows.slice().sort((a, b) => {
    const inside =
      Number(a.arrival_state === 'inside') - Number(b.arrival_state === 'inside');
    if (inside !== 0) return inside;
    const due = a.starts_at.localeCompare(b.starts_at);
    if (due !== 0) return due;

    return a.unit_label.localeCompare(b.unit_label, 'en', { numeric: true });
  });

/** "09:00–13:00" on the building's clock, or "All day". */
export const windowText = (row: ExpectedRow, zone?: string | null): string => {
  if (row.all_day) return 'All day';
  const from = clockTime(row.starts_at, zone || undefined);
  const to = clockTime(row.ends_at, zone || undefined);

  return from && to ? `${from}–${to}` : from || to;
};

export interface PillSpec {
  tone: StatusTone;
  text: string;
}

/**
 * THE ROW'S STATE, as a word and a tone (StatusPill). Arrival beats window: a
 * visitor who is inside is inside, whatever their window says.
 *
 * The last two lines are the not-signable case. The plan names only "Later
 * today"; a window that has ALREADY passed is not "later today", so it says so
 * instead of promising a time that has gone.
 */
export const pillFor = (
  row: ExpectedRow,
  zone: string | null | undefined,
  nowMs: number
): PillSpec => {
  const tz = zone || undefined;
  if (row.arrival_state === 'inside') {
    return { tone: 'neutral', text: `Inside since ${clockTime(row.arrived_at, tz)}` };
  }
  if (row.arrival_state === 'left') {
    return { tone: 'neutral', text: `Left ${clockTime(row.departed_at, tz)}` };
  }
  if (row.window_state === 'in_window') {
    return { tone: 'success', text: 'Expected now' };
  }
  if (row.window_state === 'early') {
    return {
      tone: 'warning',
      text: row.all_day ? 'Early' : `Early — due ${clockTime(row.starts_at, tz)}`,
    };
  }
  if (row.window_state === 'late') {
    return { tone: 'warning', text: `Late — was due ${windowText(row, zone)}` };
  }
  const starts = Date.parse(row.starts_at);
  if (!Number.isFinite(starts) || nowMs < starts) {
    return {
      tone: 'neutral',
      text: row.all_day
        ? 'Later today — all day'
        : `Later today — ${clockTime(row.starts_at, tz)}`,
    };
  }

  return { tone: 'neutral', text: `Window ended — was due ${windowText(row, zone)}` };
};

/** Who the visit is for: "1102 (Ana)", or just the unit when the host has no
 *  name on file. */
export const hostText = (row: ExpectedRow): string => {
  const host = (row.host?.first_name || '').trim();

  return host ? `${row.unit_label} (${host})` : row.unit_label;
};

/**
 * WHAT THE ONE TAP WILL WRITE, in one line, before it is written:
 * "Maria Lopez · Cleaner · for 1102 (Ana) · on a pass added by the building".
 * The desk confirms a record, not a button.
 */
export const describeWritten = (row: ExpectedRow): string =>
  [
    fullNameOf(row),
    row.company ? row.company : null,
    categoryLabel(row),
    `for ${hostText(row)}`,
    row.created_via === 'staff'
      ? 'on a pass added by the building'
      : 'on a pass added by the resident',
  ]
    .filter(Boolean)
    .join(' · ');

/** The search box: every token must prefix something the row carries — the
 *  visitor, their company, the unit, the host's first name or the category.
 *  Same folding as the exit desk (accents, case, O'Brien). */
export const filterExpected = (rows: ExpectedRow[], query: string): ExpectedRow[] => {
  const tokens = nameTokens(query);
  if (tokens.length === 0) return rows;

  return rows.filter(row => {
    const atoms = nameAtoms([
      `${row.first_name} ${row.last_name}`,
      row.company,
      row.unit_label,
      row.host?.first_name,
      categoryLabel(row),
    ]);

    return tokens.every(token => atoms.some(atom => atom.startsWith(token)));
  });
};

export type ExpectedScanOutcome =
  | { kind: 'none'; scanned: string }
  | { kind: 'one'; scanned: string; row: ExpectedRow }
  | { kind: 'many'; scanned: string; rows: ExpectedRow[] };

/**
 * A scanned document against today's list — by the visitor's NAME only (a
 * passport carries no unit). It narrows; it never signs anybody in.
 */
export const matchScannedPass = (
  rows: ExpectedRow[],
  firstName: string,
  lastName: string
): ExpectedScanOutcome => {
  const scanned = `${firstName || ''} ${lastName || ''}`.trim();
  const tokens = nameTokens(scanned);
  if (tokens.length === 0) return { kind: 'none', scanned };
  const matches = rows.filter(row => {
    const atoms = nameAtoms([`${row.first_name} ${row.last_name}`]);

    return tokens.every(token => atoms.some(atom => atom.startsWith(token)));
  });
  if (matches.length === 0) return { kind: 'none', scanned };
  if (matches.length === 1) return { kind: 'one', scanned, row: matches[0] };

  return { kind: 'many', scanned, rows: matches };
};

/** "Mon 28 Sept" for the desk day's calendar date ('YYYY-MM-DD'). Read at UTC
 *  noon so no reader's zone can move it to the day before. */
export const deskDateLabel = (date: string | null | undefined): string => {
  if (!date) return '';
  try {
    return formatDate(`${date}T12:00:00Z`, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });
  } catch {
    return date;
  }
};

/** The building's zone, named for a person, when its clock is NOT the tablet's
 *  own — '' otherwise. Compared by OFFSET, not by name: a browser still reports
 *  Asia/Calcutta for a building the server calls Asia/Kolkata. (The screen now
 *  names its clock always — see expected-page — so this only says whether the
 *  tablet is on a different one.) */
export const foreignZone = (zone: string | null | undefined): string => {
  const label = zoneLabel(zone);
  if (!label) return '';

  return sameClock(zone, deviceZone()) ? '' : label;
};
